<!-- 20260506 -- Copilot (Claude Sonnet 4.6) -- Public share viewer for read-only task snapshots -->
<!-- Standalone: no AppLayout, no auth, no Vuex. Minimum 320px viewport. -->
<template>
  <div class="sv-page">

    <!-- Loading -->
    <div v-if="loading" class="sv-loading" role="status" aria-label="Loading shared task">
      <div class="sv-spinner" aria-hidden="true"></div>
      <p class="sv-loading-text">Loading shared task…</p>
    </div>

    <!-- Not available (expired / revoked / invalid) -->
    <div v-else-if="!share" class="sv-unavailable">
      <div class="sv-unavail-icon" aria-hidden="true">🔒</div>
      <h1 class="sv-unavail-title">This link is no longer available</h1>
      <p class="sv-unavail-body">
        The shared task you're looking for has expired, been removed, or doesn't exist.
      </p>
      <a href="/" class="sv-unavail-link">Visit Principal Task</a>
    </div>

    <!-- Task snapshot -->
    <div v-else class="sv-content">

      <!-- Document card -->
      <div class="sv-card">

        <!-- Card header: PT brand + "Shared Task" label -->
        <div class="sv-card-header">
          <a href="/" class="sv-brand-link" title="Principal Task">
            <img src="@/assets/logos/PTLogo-SVG-NoText-Cropped.svg" alt="PT" class="sv-brand-logo" />
            <span class="sv-brand-name">Principal Task</span>
          </a>
          <span class="sv-shared-label">Shared Task</span>
        </div>

        <hr class="sv-rule" />

        <!-- Title row: title + inline star glyph -->
        <div class="sv-title-row">
          <h1 class="sv-title">{{ share.snapshot.title || 'Untitled Task' }}</h1>
          <span
            class="sv-title-star"
            :class="share.snapshot.star === 'Y' ? 'sv-title-star--filled' : 'sv-title-star--outline'"
            :aria-label="share.snapshot.star === 'Y' ? 'Starred' : 'Not starred'"
          >{{ share.snapshot.star === 'Y' ? '★' : '☆' }}</span>
        </div>

        <!-- Badges (priority + status only — star moved to title row) -->
        <div class="sv-badges" v-if="hasBadges">
          <span
            v-if="share.snapshot.priority && share.snapshot.priority !== 'neutral'"
            class="sv-badge sv-badge--priority"
            :class="`sv-badge--${share.snapshot.priority}`"
          >{{ priorityLabel(share.snapshot.priority) }}</span>

          <span v-if="share.snapshot.status" class="sv-badge sv-badge--status">
            {{ statusLabel(share.snapshot.status) }}
          </span>
        </div>

        <hr class="sv-rule sv-rule--light" />

        <!-- Metadata grid -->
        <dl class="sv-meta-grid" v-if="hasMetadata">
          <template v-if="share.snapshot.folder">
            <dt class="sv-meta-label">Folder</dt>
            <dd class="sv-meta-value">{{ share.snapshot.folder }}</dd>
          </template>
          <template v-if="share.snapshot.context">
            <dt class="sv-meta-label">Context</dt>
            <dd class="sv-meta-value">{{ share.snapshot.context }}</dd>
          </template>
          <template v-if="share.snapshot.due || share.snapshot.start">
            <template v-if="share.snapshot.start">
              <dt class="sv-meta-label">Start</dt>
              <dd class="sv-meta-value">{{ formatTaskDate(share.snapshot.start, share.snapshot.starttime) }}</dd>
            </template>
            <template v-if="share.snapshot.due">
              <dt class="sv-meta-label">Due</dt>
              <dd class="sv-meta-value">{{ formatTaskDate(share.snapshot.due, share.snapshot.duetime) }}</dd>
            </template>
          </template>
          <template v-if="share.snapshot.repeat">
            <dt class="sv-meta-label">Repeat</dt>
            <dd class="sv-meta-value">{{ repeatLabel(share.snapshot.repeat) }}</dd>
          </template>
          <template v-if="share.snapshot.remind === 'Y'">
            <dt class="sv-meta-label">Reminder</dt>
            <dd class="sv-meta-value">Set</dd>
          </template>
        </dl>

        <!-- Notes -->
        <div v-if="share.snapshot.notes" class="sv-notes-section">
          <h2 class="sv-section-title">Notes</h2>
          <div class="sv-notes-content">
            <!-- 20260508 -- Copilot (Claude Sonnet 4.6) -- Disable raw HTML to prevent stored XSS on this public unauthenticated route -->
            <vue-markdown :source="share.snapshot.notes" :html="false" />
          </div>
        </div>

        <!-- Subtasks -->
        <div
          v-if="share.includeSubtasks && share.snapshot.subtasks && share.snapshot.subtasks.length > 0"
          class="sv-subtasks-section"
        >
          <h2 class="sv-section-title">
            Subtasks
            <span class="sv-subtask-count">({{ countAllSubtasks(share.snapshot.subtasks) }})</span>
          </h2>
          <subtask-tree :subtasks="share.snapshot.subtasks" :depth="0" />
        </div>

      </div><!-- /.sv-card -->

      <!-- CTA for viewers who don't have a PT account -->
      <div class="sv-cta">
        <img src="@/assets/logos/PTLogo-SVG-NoText-Cropped.svg" alt="Principal Task" class="sv-cta-logo" />
        <div class="sv-cta-text">
          <p class="sv-cta-headline">Capture, organize, and act on everything — without the complexity.</p>
        </div>
        <a href="/" class="sv-cta-btn">Get started free →</a>
      </div>

      <!-- Footer: snapshot/expiry dates -->
      <footer class="sv-footer">
        <div class="sv-footer-meta">
          <span class="sv-footer-label">Snapshot</span>
          <span class="sv-footer-value">{{ formatDate(share.createdAt) }}</span>
          <span class="sv-footer-sep">·</span>
          <span class="sv-footer-label">Expires</span>
          <span class="sv-footer-value" :class="{ 'sv-footer-value--soon': isExpiringSoon }">
            {{ formatDate(share.expiresAt) }}
          </span>
        </div>
        <div class="sv-footer-brand">
          <a href="/" class="sv-footer-link">
            <img src="@/assets/logos/PTLogo-SVG-NoText-Cropped.svg" alt="PT" class="sv-footer-logo" />
            <span class="sv-footer-wordmark">Principal Task</span>
          </a>
        </div>
      </footer>

    </div><!-- /.sv-content -->

  </div>
</template>

<script>
import axios from 'axios';
import { h } from 'vue';
import VueMarkdown from 'vue-markdown';

// ─── SubtaskTree: local recursive component ────────────────────────────────
// 20260517 -- Copilot (Claude Sonnet 4.6) -- Converted from template string to render() so this works
// with the runtime-only Vue CLI build (no runtimeCompiler). h(SubtaskTree, ...) passes the component
// reference directly, so no string-based self-registration is required.
const SubtaskTree = {
  name: 'subtask-tree',
  props: {
    subtasks: { type: Array, default: () => [] },
    depth:    { type: Number, default: 0 },
  },
  methods: {
    priorityLabel(p) {
      const map = { negative: 'Negative', low: 'Low', medium: 'Medium', high: 'High', top: 'Top' };
      return map[p] || p;
    },
    statusLabel(s) {
      const map = {
        none: 'None', next: 'Next Action', active: 'Active', planning: 'Planning',
        delegated: 'Delegated', waiting: 'Waiting', hold: 'Hold', postponed: 'Postponed',
        someday: 'Someday', canceled: 'Canceled', reference: 'Reference',
      };
      return map[s] || s;
    },
  },
  render() {
    return h('ul', { class: 'sv-subtask-list' },
      this.subtasks.map((sub, idx) => {
        const rowChildren = [
          h('span', { class: 'sv-subtask-bullet', 'aria-hidden': 'true' }, '○'),
          h('span', { class: 'sv-subtask-title' }, sub.title),
        ];
        if (sub.priority && sub.priority !== 'neutral') {
          rowChildren.push(h('span', {
            class: ['sv-badge', 'sv-badge--priority', 'sv-badge--sm', 'sv-badge--' + sub.priority],
          }, this.priorityLabel(sub.priority)));
        }
        if (sub.status) {
          rowChildren.push(h('span', {
            class: 'sv-badge sv-badge--status sv-badge--sm',
          }, this.statusLabel(sub.status)));
        }
        const liChildren = [h('div', { class: 'sv-subtask-row' }, rowChildren)];
        if (sub.subtasks && sub.subtasks.length > 0) {
          liChildren.push(h(SubtaskTree, { subtasks: sub.subtasks, depth: this.depth + 1 }));
        }
        return h('li', {
          key: idx,
          class: 'sv-subtask-item',
          style: this.depth > 0 ? { paddingLeft: (this.depth * 20) + 'px' } : undefined,
        }, liChildren);
      })
    );
  },
};

// ─── ShareViewer ────────────────────────────────────────────────────────────
export default {
  name: 'ShareViewer',
  components: {
    'vue-markdown': VueMarkdown,
    'subtask-tree': SubtaskTree,
  },

  data() {
    return {
      loading: true,
      share:   null,
    };
  },

  computed: {
    isExpiringSoon() {
      if (!this.share) return false;
      return (this.share.expiresAt - Date.now()) < 3 * 24 * 60 * 60 * 1000;
    },

    hasBadges() {
      if (!this.share) return false;
      const s = this.share.snapshot;
      return (s.priority && s.priority !== 'neutral') || !!s.status;
    },

    hasMetadata() {
      if (!this.share) return false;
      const s = this.share.snapshot;
      return s.folder || s.context || s.due || s.start || s.repeat || s.remind === 'Y';
    },
  },

  async mounted() {
    const token = this.$route.params.token;
    await this.loadShare(token);
  },

  // 20260507 -- Copilot (Claude Sonnet 4.6) -- Reload when token param changes (Vue Router reuses component instance)
  watch: {
    '$route.params.token'(newToken) {
      if (newToken) this.loadShare(newToken);
    },
  },

  methods: {
    async loadShare(token) {
      this.loading = true;
      try {
        const res = await axios.get(`/api/public/v1/share/${token}`);
        this.share = res.data?.successful ? res.data.data : null;
      } catch (_) {
        this.share = null;
      } finally {
        this.loading = false;
      }
    },

    formatDate(epochMs) {
      if (!epochMs) return '—';
      return new Date(epochMs).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    },

    formatTaskDate(dateMs, timeMs) {
      if (!dateMs) return '—';
      // 20260508 -- Copilot (Claude Sonnet 4.6) -- When timeMs is present, derive the date from timeMs (not dateMs)
      // to avoid cross-midnight mismatches due to timezone/DST offsets
      if (timeMs && timeMs > 0) {
        const dateStr = new Date(timeMs).toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        });
        const timeStr = new Date(timeMs).toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit',
        });
        return `${dateStr} at ${timeStr}`;
      }
      return new Date(dateMs).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      });
    },

    priorityLabel(p) {
      const map = {
        negative: 'Negative Priority',
        low:      'Low Priority',
        medium:   'Medium Priority',
        high:     'High Priority',
        top:      'Top Priority',
      };
      return map[p] || p;
    },

    statusLabel(s) {
      const map = {
        none:      'None',
        next:      'Next Action',
        active:    'Active',
        planning:  'Planning',
        delegated: 'Delegated',
        waiting:   'Waiting',
        hold:      'Hold',
        postponed: 'Postponed',
        someday:   'Someday',
        canceled:  'Canceled',
        reference: 'Reference',
      };
      return map[s] || s;
    },

    repeatLabel(r) {
      const map = {
        daily:        'Daily',
        weekly:       'Weekly',
        biweekly:     'Every 2 Weeks',
        monthly:      'Monthly',
        bimonthly:    'Every 2 Months',
        quarterly:    'Quarterly',
        semiannually: 'Twice a Year',
        yearly:       'Yearly',
        advanced:     'Custom Schedule',
      };
      return map[r] || r;
    },

    countAllSubtasks(subtasks) {
      if (!subtasks) return 0;
      return subtasks.reduce((acc, sub) => acc + 1 + this.countAllSubtasks(sub.subtasks), 0);
    },
  },
};
</script>

<style scoped>
/* ── ShareViewer — self-contained CSS ────────────────────────────────────── */
/* Uses PT color palette; no dependency on custom.scss.                       */
/* Min supported viewport: 320px.                                             */

/* ── Page shell ──────────────────────────────────────────────────────────── */
.sv-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #1a2035;
  box-sizing: border-box;
}

/* ── Loading ──────────────────────────────────────────────────────────────── */
.sv-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.sv-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #dde1e9;
  border-top-color: #00b4d8;
  border-radius: 50%;
  animation: sv-spin 0.8s linear infinite;
}

@keyframes sv-spin { to { transform: rotate(360deg); } }

.sv-loading-text {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
}

/* ── Not available ────────────────────────────────────────────────────────── */
.sv-unavailable {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1.5rem;
}

.sv-unavail-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  line-height: 1;
}

.sv-unavail-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a2035;
  margin: 0 0 0.5rem;
}

.sv-unavail-body {
  color: #6b7280;
  font-size: 0.95rem;
  max-width: 380px;
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.sv-unavail-link {
  color: #00b4d8;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}

.sv-unavail-link:hover { text-decoration: underline; }

/* ── Content layout ───────────────────────────────────────────────────────── */
.sv-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 780px;
  width: 100%;
  margin: 2rem auto;
  padding: 0 1rem 2rem;
  box-sizing: border-box;
}

/* ── Document card ────────────────────────────────────────────────────────── */
.sv-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e6ed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 2rem;
}

/* ── Card header ──────────────────────────────────────────────────────────── */
/* 20260507 -- Copilot (Claude Sonnet 4.6) -- PT brand: larger logo, flex centering */
.sv-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.sv-brand-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
  transition: opacity 0.15s;
  line-height: 1;
}

.sv-brand-link:hover { opacity: 0.75; }

.sv-brand-logo {
  height: 36px;
  width: 36px;
  object-fit: contain;
  display: block; /* removes inline baseline gap */
  flex-shrink: 0;
}

.sv-brand-name {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: #1a3a52;
  line-height: 1;
}

.sv-shared-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 3px 8px;
  border-radius: 4px;
}

/* ── Divider ──────────────────────────────────────────────────────────────── */
.sv-rule {
  border: none;
  border-top: 1px solid #e2e6ed;
  margin: 0.75rem 0 1.25rem;
}

.sv-rule--light {
  border-top-color: #f0f2f5;
  margin: 1rem 0;
}

/* ── Title row + star glyph ───────────────────────────────────────────────── */
/* 20260507 -- Copilot (Claude Sonnet 4.6) -- Star moved from badge to inline glyph */
.sv-title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.sv-title {
  font-size: 1.7rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
  line-height: 1.25;
  word-break: break-word;
  letter-spacing: -0.3px;
  flex: 1;
}

.sv-title-star {
  font-size: 1.5rem;
  line-height: 1.3; /* match title leading so they baseline together */
  flex-shrink: 0;
  user-select: none;
}

.sv-title-star--filled  { color: #f59e0b; }
.sv-title-star--outline { color: #d1d5db; }

/* ── Badges ───────────────────────────────────────────────────────────────── */
.sv-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1rem;
}

.sv-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.sv-badge--sm {
  font-size: 0.68rem;
  padding: 2px 7px;
}

.sv-badge--status {
  border: 1.5px solid #d1d5db;
  color: #374151;
  background: transparent;
}

.sv-badge--star {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

/* Priority — solid fill per design spec */
.sv-badge--top      { background: #dc2626; color: #fff; }
.sv-badge--high     { background: #ea580c; color: #fff; }
.sv-badge--medium   { background: #d97706; color: #fff; }
.sv-badge--low      { background: #0284c7; color: #fff; }
.sv-badge--negative { background: #6b7280; color: #fff; }

/* ── Metadata grid ────────────────────────────────────────────────────────── */
.sv-meta-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 1.25rem;
  margin: 0 0 1rem;
  padding: 0;
}

.sv-meta-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  padding-top: 2px;
  white-space: nowrap;
}

.sv-meta-value {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.4;
  margin: 0;
}

/* ── Section titles ───────────────────────────────────────────────────────── */
.sv-section-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  margin: 1.5rem 0 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.sv-subtask-count {
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  font-size: 0.75rem;
}

/* ── Notes ────────────────────────────────────────────────────────────────── */
.sv-notes-section { margin-top: 0; }

.sv-notes-content {
  font-size: 0.9rem;
  line-height: 1.7;
  color: #374151;
}

/* Scoped markdown element styles */
.sv-notes-content :deep(h1),
.sv-notes-content :deep(h2),
.sv-notes-content :deep(h3) {
  font-size: 1rem;
  font-weight: 700;
  margin-top: 1.25rem;
  margin-bottom: 0.4rem;
  color: #111827;
}

.sv-notes-content :deep(p) { margin: 0 0 0.75rem; }

.sv-notes-content :deep(ul),
.sv-notes-content :deep(ol) {
  padding-left: 1.5rem;
  margin: 0 0 0.75rem;
}

.sv-notes-content :deep(li) { margin-bottom: 0.2rem; }

.sv-notes-content :deep(code) {
  background: #f3f4f6;
  border-radius: 3px;
  padding: 0.1em 0.35em;
  font-size: 0.85em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.sv-notes-content :deep(pre) {
  background: #1f2937;
  color: #f9fafb;
  border-radius: 6px;
  padding: 1rem;
  overflow-x: auto;
  font-size: 0.82em;
  margin: 0 0 0.75rem;
}

.sv-notes-content :deep(pre code) {
  background: transparent;
  padding: 0;
}

.sv-notes-content :deep(blockquote) {
  border-left: 3px solid #e5e7eb;
  padding-left: 1rem;
  color: #6b7280;
  margin: 0 0 0.75rem;
  font-style: italic;
}

.sv-notes-content :deep(a) { color: #00b4d8; }

.sv-notes-content :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0 0 0.75rem;
  font-size: 0.85em;
}

.sv-notes-content :deep(th),
.sv-notes-content :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 0.4rem 0.6rem;
  text-align: left;
}

.sv-notes-content :deep(th) {
  background: #f9fafb;
  font-weight: 600;
}

/* ── Subtasks ─────────────────────────────────────────────────────────────── */
.sv-subtask-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sv-subtask-item {
  margin-bottom: 0.4rem;
}

.sv-subtask-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.sv-subtask-bullet {
  color: #d1d5db;
  font-size: 0.75rem;
  flex-shrink: 0;
  line-height: 1;
}

.sv-subtask-title {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  color: #374151;
  word-break: break-word;
  line-height: 1.4;
}

/* ── CTA ──────────────────────────────────────────────────────────────────── */
/* 20260507 -- Copilot (Claude Sonnet 4.6) -- Encourages viewers to sign up */
.sv-cta {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  flex-wrap: wrap;
}

.sv-cta-logo {
  height: 36px;
  width: 36px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.sv-cta-text {
  flex: 1;
  min-width: 180px;
}

.sv-cta-headline {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e3a5f;
  margin: 0;
  line-height: 1.3;
}


.sv-cta-btn {
  display: inline-block;
  padding: 0.45rem 1rem;
  background: #0284c7;
  color: #fff;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;
}

.sv-cta-btn:hover { background: #0369a1; color: #fff; }

/* ── Footer ───────────────────────────────────────────────────────────────── */
.sv-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0 0.25rem;
}

.sv-footer-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #9ca3af;
}

.sv-footer-label {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.65rem;
  font-weight: 600;
}

.sv-footer-value {
  font-weight: 500;
  color: #6b7280;
}

.sv-footer-value--soon { color: #d97706; }

.sv-footer-sep { color: #d1d5db; }

.sv-footer-brand { display: flex; align-items: center; }

.sv-footer-link {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  color: #1a3a52;
  transition: opacity 0.15s;
}

.sv-footer-link:hover { opacity: 0.7; }

.sv-footer-logo {
  height: 20px;
  width: 20px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.sv-footer-wordmark {
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: -0.2px;
}

/* ── Mobile adjustments ───────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .sv-content {
    margin: 1rem auto;
    padding: 0 0.75rem 1.5rem;
  }

  .sv-card { padding: 1.25rem 1rem; }

  .sv-title { font-size: 1.35rem; }

  .sv-meta-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .sv-meta-label { margin-bottom: -0.3rem; }

  .sv-cta {
    padding: 0.875rem 1rem;
    gap: 0.75rem;
  }

  .sv-cta-logo { height: 28px; width: 28px; }

  .sv-cta-btn { width: 100%; text-align: center; }

  .sv-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
